import { Permissions } from '@barahpay/shared';
import type { PermissionName } from '@barahpay/shared';

export interface NavItem {
  /** Route path, also used as the menu key. */
  path: string;
  /** i18n key under `nav`. */
  labelKey: string;
  /** Icon name resolved in AdminLayout, kept out of this module so it stays data-only. */
  icon: string;
  /** The screen is listed when the user holds at least one of these. */
  permissions: PermissionName[];
  group: 'money' | 'reports' | 'customers' | 'catalog' | 'platform' | 'administration' | 'overview';
}

/**
 * The twenty navigation sections of the admin portal (spec section 44), in the order the spec lists
 * them. Investigations and Disputes are reached from the Payments and Refunds areas, and are listed
 * here so an operator can also open the queue directly (spec sections 106, 146 and 152).
 */
export const NAV_ITEMS: NavItem[] = [
  { path: '/', labelKey: 'dashboard', icon: 'dashboard', permissions: [Permissions.paymentsView], group: 'overview' },

  { path: '/payments', labelKey: 'payments', icon: 'credit-card', permissions: [Permissions.paymentsView], group: 'money' },
  {
    path: '/investigations',
    labelKey: 'investigations',
    icon: 'file-search',
    permissions: [Permissions.paymentsInvestigate, Permissions.paymentsView],
    group: 'money',
  },
  {
    path: '/payment-links',
    labelKey: 'paymentLinks',
    icon: 'link',
    permissions: [Permissions.paymentLinksView],
    group: 'money',
  },
  { path: '/refunds', labelKey: 'refunds', icon: 'rollback', permissions: [Permissions.refundsView], group: 'money' },
  { path: '/disputes', labelKey: 'disputes', icon: 'exclamation', permissions: [Permissions.disputesView], group: 'money' },
  {
    path: '/settlements',
    labelKey: 'settlements',
    icon: 'bank',
    permissions: [Permissions.settlementsView],
    group: 'money',
  },
  {
    path: '/reconciliation',
    labelKey: 'reconciliation',
    icon: 'diff',
    permissions: [Permissions.reconciliationView],
    group: 'money',
  },

  { path: '/customers', labelKey: 'customers', icon: 'team', permissions: [Permissions.customersView], group: 'customers' },
  {
    path: '/subscriptions',
    labelKey: 'subscriptions',
    icon: 'sync',
    permissions: [Permissions.subscriptionsView],
    group: 'customers',
  },
  { path: '/billing', labelKey: 'billing', icon: 'file-text', permissions: [Permissions.billingView], group: 'customers' },

  { path: '/plans', labelKey: 'plans', icon: 'appstore', permissions: [Permissions.plansView], group: 'catalog' },
  { path: '/products', labelKey: 'products', icon: 'shop', permissions: [Permissions.productsView], group: 'catalog' },

  { path: '/providers', labelKey: 'providers', icon: 'api', permissions: [Permissions.providersView], group: 'platform' },
  { path: '/webhooks', labelKey: 'webhooks', icon: 'cloud-server', permissions: [Permissions.webhooksView], group: 'platform' },
  { path: '/events', labelKey: 'events', icon: 'thunderbolt', permissions: [Permissions.eventsView], group: 'platform' },
  {
    path: '/system-health',
    labelKey: 'systemHealth',
    icon: 'heart',
    permissions: [Permissions.systemHealthView],
    group: 'platform',
  },

  {
    path: '/reports/payments',
    labelKey: 'reportsPayments',
    icon: 'trending',
    permissions: [Permissions.reportsView],
    group: 'reports',
  },
  { path: '/reports/proof', labelKey: 'reportsProof', icon: 'file-check', permissions: [Permissions.reportsView], group: 'reports' },
  {
    path: '/reports/reconciliation',
    labelKey: 'reportsReconciliation',
    icon: 'scale',
    permissions: [Permissions.reportsView],
    group: 'reports',
  },
  { path: '/reports', labelKey: 'reportsOther', icon: 'bar-chart', permissions: [Permissions.reportsView], group: 'reports' },

  { path: '/users', labelKey: 'users', icon: 'user', permissions: [Permissions.usersView], group: 'administration' },
  { path: '/roles', labelKey: 'roles', icon: 'safety', permissions: [Permissions.rolesView], group: 'administration' },
  { path: '/audit-logs', labelKey: 'auditLogs', icon: 'history', permissions: [Permissions.auditView], group: 'administration' },
  { path: '/settings', labelKey: 'settings', icon: 'setting', permissions: [Permissions.settingsView], group: 'administration' },
];

export const NAV_GROUP_LABELS: Record<NavItem['group'], string> = {
  overview: 'overview',
  money: 'money',
  reports: 'reports',
  customers: 'customers',
  catalog: 'catalog',
  platform: 'platform',
  administration: 'administration',
};
